import {AudienceType, SurveyLang, SurveyScope, SurveyPreset} from 'client/common/types';

import {AudienceTargeting} from 'client/common/utils/audience';
import {CreateSurveyParams} from 'client/common/utils/api-client/types';

import {isSimpleTemplate} from 'client/common/store/reducers/question-bank/types';

import I18N from 'assets/i18n/brandlift-project';

const i18n = I18N.surveyCreate.surveyCreator;

const DEFAULT_AUDIENCE_PARAMS: CreateSurveyParams['survey']['audience'] = {
    type: AudienceType.RespParams,
    size: AudienceTargeting.DEFAULT_AUDIENCE.size
};

function genName(preset: SurveyPreset) {
    const presetName = I18N.surveyCreate.surveyPresets.presets[preset].name;

    if (!presetName) {
        return i18n.defaultSurveyName.replace('%s', new Date().toLocaleDateString());
    }

    return i18n.surveyName
        .replace('%s', I18N.surveyCreate.surveyPresets.presets[preset].name)
        .replace('%s', new Date().toLocaleDateString());
}

// eslint-disable-next-line max-statements, complexity
export function getSurveyCreateRequestParams({
    preset,
    clientId,
    isAuthorized,
    accountId,
    countryId,
    orgId
}: {
    preset: SurveyPreset;
    clientId?: string;
    isAuthorized: boolean;
    accountId?: number;
    countryId?: number;
    orgId?: number;
}): CreateSurveyParams['survey'] {
    if (preset === SurveyPreset.YandexSerpSurvey) {
        return {
            name: i18n.defaultSurveyName.replace('%s', new Date().toLocaleDateString()),
            audience: {type: AudienceType.None, size: 150},
            clientId,
            box: {name: 'serpSurvey', context: {}},
            purpose: {
                isBox: true,
                isTemplate: false,
                selected: preset
            },
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (preset === SurveyPreset.Simple) {
        return {
            name: i18n.defaultSurveyName.replace('%s', new Date().toLocaleDateString()),
            isTemporary: !isAuthorized,
            audience: DEFAULT_AUDIENCE_PARAMS,
            clientId,
            box: {name: 'videoBox', context: {}},
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (isSimpleTemplate(preset)) {
        return {
            name: genName(preset),
            isTemporary: !isAuthorized,
            audience: DEFAULT_AUDIENCE_PARAMS,
            box: {name: 'videoBox', context: {}},
            clientId,
            purpose: {
                isBox: false,
                isTemplate: true,
                selected: preset
            },
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (preset === SurveyPreset.UX) {
        return {
            isTemporary: !isAuthorized,
            name: i18n.uxSurveyName.replace('%s', new Date().toLocaleDateString()),
            audience: {type: AudienceType.RespParams, size: 100},
            clientId,
            box: {name: 'ux', context: {task: '', questions: []}},
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (preset === SurveyPreset.Bordomatik || preset === SurveyPreset.Creative) {
        const defaultName =
            preset === SurveyPreset.Creative
                ? i18n.defaultCreativeName
                : i18n.defaultBordomatikName;

        return {
            name: defaultName.replace('%s', new Date().toLocaleDateString()),
            box: {name: preset, context: {}},
            isTemporary: !isAuthorized,
            audience: {type: AudienceType.RespParams, size: 150},
            ...(clientId && {clientId}),
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (
        preset === SurveyPreset.YandexActiveFeedback ||
        preset === SurveyPreset.YandexPassiveFeedback
    ) {
        const defaultName =
            preset === SurveyPreset.YandexActiveFeedback
                ? i18n.defaultActiveFeedbackName
                : i18n.defaultPassiveFeedbackName;

        return {
            name: defaultName.replace('%s', new Date().toLocaleDateString()),
            audience: {type: AudienceType.None, size: 150},
            clientId,
            box: {name: 'feedback', context: {type: preset}},
            purpose: {
                isBox: true,
                isTemplate: false,
                selected: preset
            },
            settings: {
                respReuse: true,
                redirects: {
                    default: ''
                },
                metrika: [],
                metrikaTitle: '',
                scope: [SurveyScope.Yandex],
                lang: SurveyLang.Ru,
                nextBtnBehaviour: 'disabledUntilValid'
            },
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    if (preset === SurveyPreset.YandexMorda) {
        return {
            name: i18n.defaultMordaFeedbackName.replace('%s', new Date().toLocaleDateString()),
            audience: {type: AudienceType.None, size: 150},
            clientId,
            box: {name: 'feedback', context: {type: preset}},
            purpose: {
                isBox: true,
                isTemplate: false,
                selected: preset
            },
            settings: {
                respReuse: true,
                redirects: {
                    default: ''
                },
                metrika: [],
                metrikaTitle: '',
                scope: [SurveyScope.Yandex],
                lang: SurveyLang.Ru,
                nextBtnBehaviour: 'disabledUntilValid',
                charLimits: {
                    disableOverflow: true,
                    longText: 1000,
                    shortText: 64,
                    answerWithText: 300
                }
            },
            ...(accountId && {accountId}),
            ...(orgId && {orgId})
        };
    }

    const params: CreateSurveyParams['survey'] = {
        name: genName(preset),
        isTemporary: !isAuthorized,
        audience: DEFAULT_AUDIENCE_PARAMS,
        clientId,
        ...(accountId && {accountId}),
        ...(orgId && {orgId})
    };

    // для PRO-шек с таргетингом на Узбекистан нет цены и его нельзя выбрать в таргетингах
    if (preset === SurveyPreset.PRO && countryId === 171) {
        Object.assign(params.audience, {
            categories: [{categoryId: 'geo', value: ['225']}]
        });
    }

    if (preset !== SurveyPreset.PRO) {
        params.purpose = {
            isBox: false,
            isTemplate: true,
            selected: preset
        };
    }

    return params;
}

export const LINK_BY_PRESET = {
    [SurveyPreset.Creative]: '/creatives',
    [SurveyPreset.Bordomatik]: '/bordomatiks',
    [SurveyPreset.CreateForMe]: '/landing#form',
    [SurveyPreset.BannerTest]: i18n.bannerTestLink,
    [SurveyPreset.Crowd]: i18n.crowdLink
} as const;

export const NEW_WINDOW_LINKS: (keyof typeof LINK_BY_PRESET)[] = [
    SurveyPreset.BannerTest,
    SurveyPreset.Crowd
];
